<script setup>
import { ref, reactive } from "vue";
import { queryStationInfoByPlantId } from "@/api/stationBaseInfo";

const emits = defineEmits(["afterSubmit"]);
// 弹窗控制
const visible = ref(false);

// 表单ref
const ruleFormRef = ref(null);

// 类型
const waterStandard = ref("");

// 遮罩
const loading = ref(false);

// 表单对象
const formData = ref({
  id: null,
  plantId: null,
  ammoniaNitrogen: null, // 氨氮
  bod: null, // 生化需氧量
  chroma: null, // 色度
  cod: null, // 化学需氧量
  phStart: null, // ph开始值
  phEnd: null, // ph结束值
  totalNitrogen: null, // 总氮
  totalPhosphorus: null, //总磷（TP）
  turbidity: null, // 浊度
  type: null, // 1:净水厂，2供水厂
  waterTemperature: null, // 水温
  ammoniaNitrogenHigh: null,
  ammoniaNitrogenLow: null,
});

// 表单自定义规则
const floatReg = /^(([1-9]{1}[0-9]{0,7})|(0{1}))(\.\d{1,2})?$/;
const validatePosNumber = (rule, value, callback) => {
  if (rule.field != "nitrogen" || rule.field != "ph") {
    if (floatReg.test(value)) {
      callback();
    } else {
      callback(new Error("输入内容必须是正数且只能保留两位小数"));
    }
  }
};

const validateMergeNum = (rule, value, callback) => {
  if (rule.field == "nitrogen") {
    const num1 = formData.value.ammoniaNitrogenHigh;
    const num2 = formData.value.ammoniaNitrogenLow;
    if (floatReg.test(num1) && floatReg.test(num2)) {
      callback();
    } else {
      callback(new Error("输入内容必须是正数且只能保留两位小数"));
    }
  } else if (rule.field == "ph") {
    const num3 = formData.value.phStart;
    const num4 = formData.value.phEnd;
    if (floatReg.test(num3) && floatReg.test(num4)) {
      callback();
    } else {
      callback(new Error("输入内容必须是正数且只能保留两位小数"));
    }
  }
};

// 表单校验规则
const purFormRule = reactive({
  cod: [{ validator: validatePosNumber, trigger: "blur" }],
  bod: [{ validator: validatePosNumber, trigger: "blur" }],
  totalNitrogen: [{ validator: validatePosNumber, trigger: "blur" }],
  chroma: [{ validator: validatePosNumber, trigger: "blur" }],
  ph: [{ validator: validateMergeNum, trigger: "blur" }],
  ammoniaNitrogen: [{ validator: validatePosNumber, trigger: "blur" }],
  waterTemperature: [{ validator: validatePosNumber, trigger: "blur" }],
  turbidity: [{ validator: validatePosNumber, trigger: "blur" }],
  totalPhosphorus: [{ validator: validatePosNumber, trigger: "blur" }],
});

const cancelPur = () => {
  ruleFormRef.value?.resetFields();
  visible.value = false;
};

const submitPur = () => {
  ruleFormRef.value?.validate((valid) => {
    if (valid) {
      emits("afterSubmit", formData.value);
      visible.value = false;
    }
  });
};

// 弹窗打开函数
const handleDialogOpen = (id, type) => {
  visible.value = true;
  waterStandard.value = type;
  loading.value = true;
  queryStationInfoByPlantId(id)
    .then((res) => {
      if (JSON.stringify(res.data) === "{}") {
        return (formData.value = {
          id: null,
          plantId: null,
          ammoniaNitrogen: null, // 氨氮
          bod: null, // 生化需氧量
          chroma: null, // 色度
          cod: null, // 化学需氧量
          phStart: null, // ph开始值
          phEnd: null, // ph结束值
          totalNitrogen: null, // 总氮
          totalPhosphorus: null, //总磷（TP）
          turbidity: null, // 浊度
          type: null, // 1:净水厂，2供水厂
          waterTemperature: null, // 水温
          ammoniaNitrogenHigh: null,
          ammoniaNitrogenLow: null,
        });
      }
      formData.value = res.data.opStandardWaterInletDTO;
    })
    .finally(() => {
      loading.value = false;
    });
};

defineExpose({
  handleDialogOpen,
});
</script>

<template>
  <el-dialog
    :close-on-click-modal="false"
    title="出水标准"
    v-model="visible"
    width="1000px"
    append-to-body
  >
    <div>
      <el-form
        label-position="top"
        label-width="80px"
        ref="ruleFormRef"
        :model="formData"
        :rules="purFormRule"
      >
        <el-row :gutter="10">
          <el-col :span="8">
            <el-form-item label="化学需氧量(CODcr)" prop="cod">
              <el-input
                placeholder="请输入"
                v-model="formData.cod"
                maxlength="8"
              >
                <template #append>mg/L</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="生化需氧量(BOD5)" prop="bod">
              <el-input
                placeholder="请输入"
                v-model="formData.bod"
                maxlength="8"
              >
                <template #append>mg/L</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="氨氮" prop="ammoniaNitrogen">
              <el-input
                placeholder="请输入"
                v-model="formData.ammoniaNitrogen"
                maxlength="8"
              >
                <template #append>mg/L</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10" class="formRow">
          <el-col :span="8">
            <el-form-item label="总氮(TN)" prop="totalNitrogen">
              <el-input
                placeholder="请输入"
                v-model="formData.totalNitrogen"
                maxlength="8"
              >
                <template #append>mg/L</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="总磷(TP)" prop="totalPhosphorus">
              <el-input
                placeholder="请输入"
                v-model="formData.totalPhosphorus"
                maxlength="8"
              >
                <template #append>mg/L</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="浊度" prop="turbidity">
              <el-input
                placeholder="请输入"
                v-model="formData.turbidity"
                maxlength="8"
              >
                <template #append>度</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10" class="formRow">
          <el-col :span="8">
            <el-form-item label="色度(稀释倍数)" prop="chroma">
              <el-input
                placeholder="请输入"
                v-model="formData.chroma"
                maxlength="8"
              >
                <template #append>mg/L</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="pH" prop="ph">
              <el-row :gutter="10">
                <el-col :span="12">
                  <el-input
                    placeholder="请输入"
                    v-model="formData.phStart"
                    maxlength="8"
                  >
                    <template #prepend>上限:</template>
                  </el-input>
                </el-col>
                <el-col :span="12">
                  <el-input
                    placeholder="请输入"
                    v-model="formData.phEnd"
                    maxlength="8"
                  >
                    <template #prepend>下限:</template>
                  </el-input>
                </el-col>
              </el-row>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="水温" prop="waterTemperature">
              <el-input
                placeholder="请输入"
                v-model="formData.waterTemperature"
                maxlength="8"
              >
                <template #append>℃</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>
    <template #footer>
      <div class="dialog-footer">
        <el-button size="small" @click="cancelPur">取 消</el-button>
        <el-button size="small" type="primary" @click="submitPur"
          >确 定</el-button
        >
      </div>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped></style>
